import { queryKeys } from '@inbox-demo-nx/constants';
import { useSendBirdContext } from '@inbox-demo-nx/contexts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import SendBird from 'sendbird';

import { sortInboxItems } from './utils';

const QUERY_KEY = [queryKeys.inbox];

export type Message =
  | SendBird.AdminMessage
  | SendBird.UserMessage
  | SendBird.FileMessage;

export type InboxItem = {
  channel: SendBird.GroupChannel;
  recentMessages?: Message[];
};

function useConnectionStatus() {
  const { sendbird } = useSendBirdContext();
  const [connectionStatus, setConnectionStatus] = useState(
    sendbird.getConnectionState()
  );

  useEffect(() => {
    setConnectionStatus(sendbird.getConnectionState());

    const connectionHandler = new sendbird.ConnectionHandler();
    const updateConnectionStatus = (log: string) => () => {
      console.info(`Connection - ${log}`);
      setConnectionStatus(sendbird.getConnectionState());
    };

    connectionHandler.onReconnectFailed = updateConnectionStatus('Failed');
    connectionHandler.onReconnectStarted = updateConnectionStatus('Started');
    connectionHandler.onReconnectSucceeded =
      updateConnectionStatus('Succeeded');

    const connectionHandlerId = 'inboxConnectionHandler';
    sendbird.addConnectionHandler(connectionHandlerId, connectionHandler);

    return () => {
      sendbird.removeConnectionHandler(connectionHandlerId);
    };
  }, [sendbird]);

  return useMemo(() => {
    return { isConnected: connectionStatus === 'OPEN', connectionStatus };
  }, [connectionStatus]);
}

function createChannelListQuery() {
  const listQuery =
    SendBird.getInstance().GroupChannel.createMyGroupChannelListQuery();
  listQuery.memberStateFilter = 'joined_only';
  listQuery.order = 'latest_last_message';
  listQuery.limit = 100;
  return listQuery;
}

const replaceInboxItemChannel = (
  item: InboxItem,
  channel: SendBird.GroupChannel
): InboxItem => {
  return { ...item, channel };
};

export default function useInboxChannelList({
  shouldIncludeRecentMessages,
  includeEmptyChannels = false,
}: {
  shouldIncludeRecentMessages?: (channel: SendBird.GroupChannel) => boolean;
  includeEmptyChannels?: boolean;
} = {}) {
  const { isConnected } = useConnectionStatus();
  const {
    data = [],
    isLoading,
    refetch,
    error,
    dataUpdatedAt,
  } = useQuery(
    QUERY_KEY,
    function getInboxItems() {
      const promise = new Promise<InboxItem[]>((resolve, reject) => {
        const listQuery = createChannelListQuery();
        listQuery.includeEmpty = includeEmptyChannels;
        listQuery.next(async (channels, error) => {
          if (error) {
            reject(error);
            return;
          }

          const recentMessages =
            shouldIncludeRecentMessages &&
            (await Promise.all(
              channels.filter(shouldIncludeRecentMessages).map(
                (channel) =>
                  new Promise<
                    [
                      string,
                      (
                        | SendBird.UserMessage
                        | SendBird.FileMessage
                        | SendBird.AdminMessage
                      )[]
                    ]
                  >((resolve) => {
                    const listQuery = channel.createPreviousMessageListQuery();
                    listQuery.limit = 3;
                    listQuery.messageTypeFilter = 'MESG';
                    listQuery.reverse = true;
                    listQuery.load((messages, originalError) => {
                      if (originalError) {
                        const error = new Error(
                          'Fetching recent messages failed.',
                          // @ts-ignore
                          { cause: originalError }
                        );
                        reject(error);
                        return;
                      }
                      resolve([channel.url, messages]);
                    });
                  })
              )
            ).then((recentMessages) => Object.fromEntries(recentMessages)));

          const inboxItems = channels.map((channel) => ({
            inboxItemType: 'channel' as const,
            channel,
            recentMessages: recentMessages?.[channel.url],
          }));

          resolve(sortInboxItems(inboxItems));
        });
      });
      return promise;
    },
    { enabled: false }
  );
  const latestDataRef = useRef(data);
  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  const queryClient = useQueryClient();
  const { sendbird } = useSendBirdContext();

  useEffect(() => {
    const channelHandler = new sendbird.ChannelHandler();

    function removeChannel(deletedChannelUrl: string) {
      queryClient.setQueryData<InboxItem[]>(QUERY_KEY, (data) => {
        if (!data) {
          return [];
        }
        return data.filter(({ channel }) => {
          return channel.url !== deletedChannelUrl;
        });
      });
    }

    channelHandler.onUserJoined = (channel, user) => {
      if (user.userId === sendbird.currentUser.userId) {
        refetch();
      }
    };

    channelHandler.onUserLeft = (channel, user) => {
      if (user.userId === sendbird.currentUser.userId) {
        removeChannel(channel.url);
      }
    };

    channelHandler.onChannelChanged = (changedChannel) => {
      if (!changedChannel.isGroupChannel()) {
        return;
      }

      const targetChannel = latestDataRef.current.find(
        (item) => item.channel.url === changedChannel.url
      );

      if (!targetChannel) {
        queryClient.refetchQueries(QUERY_KEY);
        return;
      }

      queryClient.setQueryData<InboxItem[]>(QUERY_KEY, (data) => {
        if (!data) {
          return [];
        }
        return sortInboxItems(
          data.map((item) => {
            const { channel } = item;
            return channel.url === changedChannel.url
              ? replaceInboxItemChannel(item, changedChannel)
              : item;
          })
        );
      });
    };

    channelHandler.onChannelDeleted = (deletedChannelUrl) => {
      removeChannel(deletedChannelUrl);
    };

    sendbird.addChannelHandler('inboxChannelHandler', channelHandler);

    return () => {
      sendbird.removeChannelHandler('inboxChannelHandler');
    };
  }, [queryClient, refetch, sendbird]);

  return useMemo(
    () => ({
      data,
      isLoading: !isConnected || isLoading,
      refetch,
      error,
      dataUpdatedAt,
    }),
    [data, isConnected, isLoading, refetch, error, dataUpdatedAt]
  );
}
